import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
  Renderer2,
} from '@angular/core';

/**
 * Wraps native number inputs with app-themed ▲/▼ steppers (single control chrome).
 * Skipped when {@link PxlNumberStepperDirective.OPT_OUT_ATTR} is set.
 */
@Directive({
  standalone: true,
  selector: 'input[type="number"]',
})
export class PxlNumberStepperDirective implements AfterViewInit, OnDestroy {
  static readonly OPT_OUT_ATTR = 'data-pxl-no-stepper';

  private cleanup?: () => void;

  constructor(
    private readonly el: ElementRef<HTMLInputElement>,
    private readonly renderer: Renderer2,
  ) {}

  ngAfterViewInit(): void {
    const input = this.el.nativeElement;
    if (input.hasAttribute(PxlNumberStepperDirective.OPT_OUT_ATTR)) return;
    if (input.closest('.pxl-number-wrap')) return;

    const parent = input.parentNode;
    if (!parent) return;

    const wrap = this.renderer.createElement('div');
    this.renderer.addClass(wrap, 'pxl-number-wrap');

    if (input.classList.contains('float-dim-input') || input.classList.contains('pxl-input-sm')) {
      this.renderer.addClass(wrap, 'pxl-number-wrap--compact');
    }
    if (this.shouldFillWidth(input)) {
      this.renderer.addClass(wrap, 'pxl-number-wrap--fill');
    }
    if (input.closest('.shape-exact-row')) {
      this.renderer.addClass(wrap, 'pxl-number-wrap--shape');
    }
    if (input.closest('.import-panel')) {
      this.renderer.addClass(wrap, 'pxl-number-wrap--accent2');
    }
    if (input.closest('.floating-hint')) {
      this.renderer.addClass(wrap, 'pxl-number-wrap--floating');
    }

    const stepper = this.renderer.createElement('div');
    this.renderer.addClass(stepper, 'pxl-number-stepper');

    const btnUp = this.makeStepBtn('up', 'Increase value');
    const btnDown = this.makeStepBtn('down', 'Decrease value');

    this.renderer.appendChild(stepper, btnUp);
    this.renderer.appendChild(stepper, btnDown);

    this.renderer.insertBefore(parent, wrap, input);
    this.renderer.appendChild(wrap, input);
    this.renderer.appendChild(wrap, stepper);

    const syncDisabled = () => this.syncStepDisabled(input, btnUp, btnDown);

    const onStepUp = (ev: Event) => {
      ev.preventDefault();
      ev.stopPropagation();
      this.applyStep(input, 1);
      syncDisabled();
    };
    const onStepDown = (ev: Event) => {
      ev.preventDefault();
      ev.stopPropagation();
      this.applyStep(input, -1);
      syncDisabled();
    };
    const onInput = () => syncDisabled();

    btnUp.addEventListener('click', onStepUp);
    btnDown.addEventListener('click', onStepDown);
    input.addEventListener('input', onInput);

    syncDisabled();

    this.cleanup = () => {
      btnUp.removeEventListener('click', onStepUp);
      btnDown.removeEventListener('click', onStepDown);
      input.removeEventListener('input', onInput);
    };
  }

  ngOnDestroy(): void {
    this.cleanup?.();
  }

  private shouldFillWidth(input: HTMLInputElement): boolean {
    const sw = input.style.width;
    const hasFixedInline = !!sw && sw !== '100%' && !sw.trim().endsWith('%');
    return (
      !!input.closest('.shape-exact-row') ||
      !!input.closest('.import-row') ||
      input.classList.contains('vid-maxframes-input') ||
      (input.classList.contains('pxl-input') && !hasFixedInline)
    );
  }

  private makeStepBtn(kind: 'up' | 'down', label: string): HTMLButtonElement {
    const btn = this.renderer.createElement('button') as HTMLButtonElement;
    this.renderer.setAttribute(btn, 'type', 'button');
    this.renderer.setAttribute(btn, 'tabindex', '-1');
    this.renderer.setAttribute(btn, 'aria-label', label);
    this.renderer.addClass(btn, 'pxl-num-btn');
    this.renderer.addClass(btn, kind === 'up' ? 'pxl-num-up' : 'pxl-num-down');
    const d = kind === 'up' ? 'M1.5 6L5 2.5 8.5 6' : 'M1.5 2L5 5.5 8.5 2';
    btn.innerHTML = `<svg width="9" height="6" viewBox="0 0 10 8" fill="none" aria-hidden="true"><path d="${d}" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    return btn;
  }

  private applyStep(input: HTMLInputElement, dir: 1 | -1): void {
    if (input.disabled || input.readOnly) return;

    const stepAttr = input.getAttribute('step');
    const step = stepAttr != null && stepAttr !== '' ? parseFloat(stepAttr) : 1;
    const safeStep = Number.isFinite(step) && step > 0 ? step : 1;

    let v = parseFloat(input.value);
    if (Number.isNaN(v)) {
      const minAttr = input.getAttribute('min');
      v = minAttr != null && minAttr !== '' ? parseFloat(minAttr) : 0;
    }

    let next = v + dir * safeStep;
    const minAttr = input.getAttribute('min');
    const maxAttr = input.getAttribute('max');
    const min = minAttr != null && minAttr !== '' ? parseFloat(minAttr) : NaN;
    const max = maxAttr != null && maxAttr !== '' ? parseFloat(maxAttr) : NaN;
    if (!Number.isNaN(min)) next = Math.max(min, next);
    if (!Number.isNaN(max)) next = Math.min(max, next);

    if (safeStep >= 1 && Number.isInteger(safeStep)) {
      next = Math.round(next);
    }

    const str = String(next);
    if (input.value !== str) {
      input.value = str;
    }
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  private syncStepDisabled(
    input: HTMLInputElement,
    btnUp: HTMLButtonElement,
    btnDown: HTMLButtonElement,
  ): void {
    if (input.disabled || input.readOnly) {
      btnUp.disabled = true;
      btnDown.disabled = true;
      return;
    }

    const v = parseFloat(input.value);
    const minAttr = input.getAttribute('min');
    const maxAttr = input.getAttribute('max');
    const min = minAttr != null && minAttr !== '' ? parseFloat(minAttr) : NaN;
    const max = maxAttr != null && maxAttr !== '' ? parseFloat(maxAttr) : NaN;

    if (!Number.isNaN(v)) {
      btnDown.disabled = !Number.isNaN(min) && v <= min;
      btnUp.disabled = !Number.isNaN(max) && v >= max;
    } else {
      btnDown.disabled = false;
      btnUp.disabled = false;
    }

    // When empty, still allow stepping from implicit min
    if (Number.isNaN(v) && !Number.isNaN(min)) {
      btnDown.disabled = true;
    }
  }
}
